import { useCallback } from "react"
import { DeepReadonly } from "ts-essentials"
import { Compare } from "@mui/icons-material"
import InstrumentSelector from "connected-components/instrument-selector"
import { IsoDate } from "helpers/types/dates"
import { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import useOpenUnifiedSearch from "connected-components/dashboard/hooks/use-open-unified-search"
import { MODE_SELECTOR_OPTIONS, SELECTED_MODE_VALUES, resolveFxHedgeEnabled } from "../../helpers"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "../../analytics"
import type { InstrumentSelectionData } from "../../analytics"
import ComparisonMenu from "../comparison-menu"
import { RULES, getIsFxHedgeSupportedByModel } from "connected-components/compare/position-sets"
import { PositionSetWeightingRule, RuleWeightingStrategyType, WeightingGroupingType } from "helpers/types/instruments/selected"
import { useResourceCurrency } from "resources/currencies"
import { useModelMetadata } from "resources/models"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../../store/core-slice"
import { DEFAULT_OPTIMIZATION_STATE } from "../../store/optimization-slice"
import { useCoreActions } from "../../hooks/use-core-actions"
import CompareFxHedgeMenu from "./compare-fx-hedge-menu"
import FxHedgedChip from "./fx-hedged-chip"

export default function DashboardTopBarCompare({ comparisonInstrumentMetadata, adjustedStartDate }: DeepReadonly<DashboardTopBarCompareProps>) {
  const analytics = useAnalytics()
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const {
    instrument: mainInstrument,
    comparisonInstrument,
    secondInstrument,
    endDate,
    isEndVsStartDateComparison,
  } = coreState
  const { isOptimizePanelExpanded } = useAppSelector((s) => s.dashboard.optimization[dashboardId] ?? DEFAULT_OPTIMIZATION_STATE)
  const { setComparisonInstrument, setSelectedMode, setIsEndVsStartDateComparison, setIsOptimizePanelExpanded } = useCoreActions()

  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const [modelMetadata] = useModelMetadata(modelId as string)
  // Currency comes from the main instrument, applied to the comparison — mirrors compare/index.jsx.
  const [currency] = useResourceCurrency(modelId, mainInstrument)
  const isFxHedgeSupported = getIsFxHedgeSupportedByModel(modelMetadata?.factors, currency)

  const onComparisonInstrumentSelection = useCallback(({ id, name, type, searchType }: InstrumentSelectionData) => {
    analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_SELECTION, { instrument: { id, name, type }, searchType })
  }, [analytics])

  const openSearch = useOpenUnifiedSearch(setComparisonInstrument, RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD, mainInstrument, true, false, onComparisonInstrumentSelection)

  const handleOpenComparisonSearch = useCallback(() => {
    analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_OPENED)

    openSearch()
  }, [analytics, openSearch])

  const handleSetIsEndVsStartDateComparison = useCallback(() => {
    setIsEndVsStartDateComparison(true)
    setComparisonInstrument(mainInstrument)
    setSelectedMode(MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.endVsStart].id)
  }, [setIsEndVsStartDateComparison, setComparisonInstrument, mainInstrument, setSelectedMode])

  const handleSetWeightingStrategy = useCallback((weightingStrategyType: RuleWeightingStrategyType) => {
    if (!mainInstrument || mainInstrument.type === undefined) {
      throw new Error("Cannot set weighting strategy for this instrument type")
    }

    let weightingRule: PositionSetWeightingRule
    if (weightingStrategyType === "equalWeighting") {
      weightingRule = { equalWeighting: { groupBy: WeightingGroupingType.LONG_SHORT } }
    } else if (weightingStrategyType === "idioWeighting") {
      weightingRule = { idioWeighting: true }
    } else {
      weightingStrategyType satisfies never
      throw new Error(`Unrecognized weighting strategy type: ${weightingStrategyType}`)
    }

    setComparisonInstrument({
      type: RULES,
      id: undefined,
      rulesConfiguration: {
        source: { positionSet: mainInstrument },
        rules: [
          { weighting: weightingRule },
        ],
      },
    })
  }, [mainInstrument, setComparisonInstrument])

  const handleComparisonRemove = useCallback(() => {
    const comparisonId = comparisonInstrument && comparisonInstrument.type !== RULES ? comparisonInstrument.id : undefined
    analytics.track(DASHBOARD_EVENTS.COMPARISON_REMOVED, {
      comparisonInstrumentId: comparisonId,
      comparisonInstrumentName: comparisonInstrumentMetadata?.name,
    })
    setIsEndVsStartDateComparison(false)
    setComparisonInstrument(null)
    setSelectedMode(secondInstrument ? "rebalanced" : "original")
  }, [setComparisonInstrument, comparisonInstrument, comparisonInstrumentMetadata, setIsEndVsStartDateComparison, setSelectedMode, secondInstrument, analytics])

  const handleToggleFxHedge = useCallback((enabled: boolean) => {
    setComparisonInstrument({ ...comparisonInstrument, fxHedgeEnabled: enabled })
  }, [comparisonInstrument, setComparisonInstrument])

  if (!comparisonInstrument) {
    return <ComparisonMenu
      onInstrumentClick={handleOpenComparisonSearch}
      onDateComparisonClick={handleSetIsEndVsStartDateComparison}
      isOptimizePanelExpanded={isOptimizePanelExpanded}
      setIsOptimizePanelExpanded={setIsOptimizePanelExpanded}
      onSetWeightStrategy={handleSetWeightingStrategy}
    />
  }

  if (!comparisonInstrumentMetadata) {
    return null
  }

  const isWeightStrategyActive = comparisonInstrument?.type === RULES

  const effectiveFxHedgeEnabled = resolveFxHedgeEnabled(comparisonInstrumentMetadata, comparisonInstrument) && isFxHedgeSupported

  return (
    <InstrumentSelector
      icon={<Compare />}
      selectedInstrumentMetadata={comparisonInstrumentMetadata}
      openSearch={openSearch}
      onClear={handleComparisonRemove}
      disabled={isWeightStrategyActive}
      trailingActions={<>
        {effectiveFxHedgeEnabled && <FxHedgedChip />}
        <CompareFxHedgeMenu fxHedgeEnabled={effectiveFxHedgeEnabled} isFxHedgeSupported={isFxHedgeSupported} onToggle={handleToggleFxHedge} />
      </>}
      {...(isEndVsStartDateComparison ? {
        startDate: adjustedStartDate,
        endDate: endDate,
      } : {})}
    />
  )
}

interface DashboardTopBarCompareProps {
  adjustedStartDate?: IsoDate
  comparisonInstrumentMetadata?: InstrumentMetadata
}
